import React, { useCallback, useEffect, useRef, useState } from 'react';
import { API_BASE } from '../config';
import '../styles/procurement-admin.css';

function newId() {
  const bytes = crypto.getRandomValues(new Uint8Array(16));
  bytes[6] = (bytes[6] & 15) | 64;
  bytes[8] = (bytes[8] & 63) | 128;
  const hex = Array.from(bytes, byte => byte.toString(16).padStart(2, '0')).join('');
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}
const emptyForm = () => ({ name: '', email: '', password: '' });

export default function WarehouseManagers() {
  const [rows, setRows] = useState([]);
  const [form, setForm] = useState(emptyForm);
  const [loading, setLoading] = useState(true);
  const [verified, setVerified] = useState(false);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState('');
  const running = useRef(false);
  const pending = useRef(new Map());

  const request = useCallback(async (path, command) => {
    const response = await fetch(`${API_BASE}/procurement/v1${path}`, {
      method: command ? 'POST' : 'GET',
      headers: { Authorization: localStorage.getItem('token'), 'Content-Type': 'application/json' },
      ...(command ? { body: JSON.stringify(command) } : {}),
    });
    const result = await response.json().catch(() => ({}));
    if (!response.ok) throw new Error(result.message || 'Procurement request failed');
    return result;
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const result = await request('/bootstrap');
      if (result.context?.role !== 'FPO_ADMIN' || !Array.isArray(result.warehouse_managers)) {
        throw new Error('Warehouse Manager identity data could not be verified.');
      }
      setRows(result.warehouse_managers);
      setVerified(true);
    } catch (error) {
      setRows([]);
      setVerified(false);
      throw error;
    } finally { setLoading(false); }
  }, [request]);

  useEffect(() => { load().catch(error => setMessage(error.message)); }, [load]);

  async function submit(action, entityId, data, scope, successMessage, onSuccess) {
    if (running.current || !verified) return;
    running.current = true;
    setBusy(true);
    setMessage('');
    const fingerprint = JSON.stringify({ action, entityId, data });
    try {
      if (pending.current.get(scope)?.fingerprint !== fingerprint) {
        pending.current.set(scope, { fingerprint, command: {
          command_id: newId(), entity_id: entityId || newId(), action, data,
        } });
      }
      await request('/commands', pending.current.get(scope).command);
      pending.current.delete(scope);
      if (onSuccess) onSuccess();
      try { await load(); setMessage(successMessage); }
      catch { setMessage(`${successMessage} The list could not be refreshed; please refresh this page.`); }
    } catch (error) {
      // Retain the immutable command for an explicit retry, including lost responses.
      try { await load(); setMessage(error.message); }
      catch { setMessage(`${error.message} The list could not be verified; please refresh this page.`); }
    } finally { running.current = false; setBusy(false); }
  }

  async function createManager(event) {
    event.preventDefault();
    if (running.current || !verified) return;
    const name = form.name.trim(), login = form.email.trim().toLowerCase();
    if (!name || !login || !form.password) { setMessage('Name, email, and password are required.'); return; }
    if (login.length > 254 || !/^[-a-z0-9.!#$%&'*+/=?^_`{|}~]+@(?:[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?\.)+[a-z]{2,63}$/i.test(login)) {
      setMessage('Enter a valid email address.'); return;
    }
    if (form.password.length < 12) { setMessage('Password must contain at least 12 characters.'); return; }
    await submit('membership.create', null, { role: 'WAREHOUSE_MANAGER', name, login, password: form.password },
      'create', 'Warehouse Manager created.', () => setForm(emptyForm()));
  }

  async function changeMembership(row) {
    if (running.current || !verified || typeof row.membership_active !== 'boolean') return;
    const active = !row.membership_active;
    if (!window.confirm(`${active ? 'Reactivate' : 'Deactivate'} Warehouse Manager ${row.name}'s membership? ${active ? 'Only the membership is reactivated; a disabled actor remains disabled.' : 'Warehouse assignments and receipt history will be retained.'}`)) return;
    const action = `warehouse.manager.membership.${active ? 'activate' : 'deactivate'}`;
    await submit(action, row.membership_id, {}, `${action}:${row.membership_id}`,
      `Warehouse Manager membership ${active ? 'reactivated' : 'deactivated'}.`);
  }

  async function resetPassword(row) {
    if (running.current || !verified || row.membership_active !== true) return;
    const password = window.prompt(`Enter a new password for ${row.name}. Use at least 12 characters; the server validates the maximum of 72 UTF-8 bytes.`);
    if (password === null) return;
    if (password.length < 12) { setMessage('Password must contain at least 12 characters.'); return; }
    if (!window.confirm(`Reset the password for ${row.name}?`)) return;
    await submit('warehouse.manager.password.reset', row.membership_id, { password },
      `reset:${row.membership_id}`, 'Warehouse Manager password reset.');
  }

  const disabled = busy || loading || !verified;
  return <section className="proc-admin proc-admin--identity-management" aria-label="Warehouse Managers">
    <header className="proc-admin-identity-header">
      <div>
        <h2>Warehouse Managers</h2>
        <p>Manage standalone Warehouse Manager identities for the current FPO.</p>
      </div>
      <div className="proc-admin-identity-header-actions">
        <button className="proc-admin-identity-button proc-admin-identity-button--secondary" type="button" disabled={busy || loading} onClick={() => {
          setMessage(''); load().catch(error => setMessage(error.message));
        }}>Refresh</button>
      </div>
    </header>
    <p className="proc-admin-identity-guidance">Warehouse assignments remain under <strong>Procurement → Warehouses</strong>.</p>
    {message && <p className="proc-admin-identity-message" role="status">{message}</p>}
    <form className="proc-admin-identity-card proc-admin-identity-form proc-admin-identity-form--warehouse-manager" aria-label="Create Warehouse Manager" onSubmit={createManager}>
      <h3>Create Warehouse Manager</h3>
      <label>Manager name *<input aria-label="Manager name" required disabled={disabled} value={form.name} onChange={event => setForm(current => ({ ...current, name: event.target.value }))} /></label>
      <label>Email *<input aria-label="Manager email" type="text" inputMode="email" required disabled={disabled} value={form.email} onChange={event => setForm(current => ({ ...current, email: event.target.value }))} /></label>
      <label>Initial password *<input aria-label="Manager password" type="password" autoComplete="new-password" required minLength={12} disabled={disabled} value={form.password} onChange={event => setForm(current => ({ ...current, password: event.target.value }))} /></label>
      <p>Use at least 12 characters. The server validates the maximum of 72 UTF-8 bytes.</p>
      <button className="proc-admin-identity-button proc-admin-identity-button--primary" type="submit" disabled={disabled}>{busy ? 'Saving…' : 'Create Warehouse Manager'}</button>
    </form>
    <section className="proc-admin-identity-card" aria-label="Warehouse Manager list">
      <h3>Warehouse Manager memberships</h3>
      {loading ? <p className="proc-admin-identity-state">Loading Warehouse Managers…</p> : verified && (rows.length === 0
        ? <p className="proc-admin-identity-state">No Warehouse Managers configured in this FPO.</p>
        : <div className="proc-admin-identity-table-scroll" role="region" aria-label="Warehouse Manager table" tabIndex={0}><table className="proc-admin-identity-table">
          <thead><tr><th>Name</th><th>Email</th><th>Membership</th><th>Identity</th><th>Actions</th></tr></thead>
          <tbody>{rows.map(row => {
            const membershipActive = row.membership_active === true;
            const identity = row.actor_active === true ? (membershipActive ? 'Available' : 'Unavailable') : 'Actor disabled';
            return <tr key={row.membership_id}>
              <td>{row.name}</td><td>{row.login}</td>
              <td><span className={`proc-admin-identity-badge ${membershipActive ? 'proc-admin-identity-badge--active' : 'proc-admin-identity-badge--inactive'}`}>{membershipActive ? 'Active' : 'Inactive'}</span></td>
              <td><span className={`proc-admin-identity-badge ${identity === 'Available' ? 'proc-admin-identity-badge--available' : 'proc-admin-identity-badge--inactive'}`}>{identity}</span></td>
              <td><div className="proc-admin-identity-row-actions">
                <button className={`proc-admin-identity-button ${membershipActive ? 'proc-admin-identity-button--danger' : 'proc-admin-identity-button--secondary'}`} type="button" disabled={disabled || typeof row.membership_active !== 'boolean'} onClick={() => changeMembership(row)}>{membershipActive ? 'Deactivate' : 'Reactivate'}</button>
                <button className="proc-admin-identity-button proc-admin-identity-button--secondary" type="button" disabled={disabled || !membershipActive} onClick={() => resetPassword(row)}>Reset password</button>
              </div></td>
            </tr>;
          })}</tbody>
        </table></div>)}
    </section>
  </section>;
}
